import { dayKey, formatDayLabel } from '../../lib/format'
import type { TimelineMessage } from '../../sync/outboxMerge'

export type TimelineEntry =
  | { kind: 'day'; key: string; label: string }
  | { kind: 'new'; key: string }
  | {
      kind: 'message'
      key: string
      item: TimelineMessage
      continued: boolean
      /** An answer to a bot's question (or a checklist's Done) directly under it: the question is right there, no quote. */
      answersAbove: boolean
    }

export const GROUP_WINDOW_MS = 5 * 60 * 1000

export interface TimelineOptions {
  meId: string
  /** Show "New messages" before the first message from others with seq greater than this. */
  newAfterSeq: number | null
  now?: Date
}

/** Day dividers, the "New messages" divider and author grouping for the message list. */
export function buildTimeline(items: TimelineMessage[], options: TimelineOptions): TimelineEntry[] {
  const entries: TimelineEntry[] = []
  let previous: TimelineMessage | null = null
  let previousDay: string | null = null
  let newPlaced = false

  for (const item of items) {
    const { message } = item
    const day = dayKey(message.createdAt)
    let breakGroup = false

    if (day !== previousDay) {
      entries.push({ kind: 'day', key: `day-${day}`, label: formatDayLabel(message.createdAt, options.now) })
      previousDay = day
      breakGroup = true
    }

    if (
      !newPlaced &&
      options.newAfterSeq !== null &&
      !item.pending &&
      message.authorId !== options.meId &&
      message.seq > options.newAfterSeq
    ) {
      entries.push({ kind: 'new', key: 'new-messages' })
      newPlaced = true
      breakGroup = true
    }

    const continued =
      !breakGroup &&
      previous !== null &&
      previous.message.authorId === message.authorId &&
      !previous.message.deletedAt &&
      new Date(message.createdAt).getTime() - new Date(previous.message.createdAt).getTime() < GROUP_WINDOW_MS

    const answersAbove = !breakGroup && previous !== null && Boolean(message.answer) && message.replyToId === previous.message.id

    entries.push({ kind: 'message', key: message.id, item, continued, answersAbove })
    previous = item
  }
  return entries
}
